// UK tax-year + HMRC MTD ITSA standard (fixed) quarterly-period date utilities.
// The UK tax year runs 6 Apr (year N) to 5 Apr (year N+1) — NOT the calendar year — and
// the four standard ITSA quarterly update periods are fixed to that boundary:
//   Q1  6 Apr – 5 Jul   Q2  6 Jul – 5 Oct   Q3  6 Oct – 5 Jan   Q4  6 Jan – 5 Apr
// All parsing/formatting is done in UTC so an ISO date-only string (e.g. '2025-04-06',
// as sent/received by the HmrcMtdItsa* endpoints) is never shifted by the local timezone.

const TAX_YEAR_START_MONTH = 3 // April, 0-indexed
const TAX_YEAR_START_DAY = 6

const toUtcDate = (value: Date | string): Date => (value instanceof Date ? value : new Date(value))

// Returns the HMRC-style "YYYY-YY" tax year label for a date, e.g. periodStartDate
// '2025-04-06' -> '2025-26'; '2025-04-05' (the day before the boundary) -> '2024-25'.
export const deriveTaxYear = (value: Date | string): string => {
  const date = toUtcDate(value)
  const year = date.getUTCFullYear()
  const isBeforeTaxYearStart =
    date.getUTCMonth() < TAX_YEAR_START_MONTH ||
    (date.getUTCMonth() === TAX_YEAR_START_MONTH && date.getUTCDate() < TAX_YEAR_START_DAY)
  const startYear = isBeforeTaxYearStart ? year - 1 : year
  const endYearShort = String((startYear + 1) % 100).padStart(2, '0')
  return `${startYear}-${endYearShort}`
}

export type QuarterIndex = 1 | 2 | 3 | 4

type QuarterDefinition = {
  index: QuarterIndex
  label: string
  // Inclusive (month, day) tuples the period spans — month is 0-indexed. `wraps` marks
  // periods that cross the Dec/Jan calendar-year boundary (Q3 and Q4 do not wrap the
  // *tax* year, but Q3 spans Oct-Jan and Q4 spans Jan-Apr of the calendar year).
  startMonth: number
  startDay: number
  endMonth: number
  endDay: number
}

const QUARTERS: QuarterDefinition[] = [
  { index: 1, label: 'Q1 (6 Apr – 5 Jul)', startMonth: 3, startDay: 6, endMonth: 6, endDay: 5 },
  { index: 2, label: 'Q2 (6 Jul – 5 Oct)', startMonth: 6, startDay: 6, endMonth: 9, endDay: 5 },
  { index: 3, label: 'Q3 (6 Oct – 5 Jan)', startMonth: 9, startDay: 6, endMonth: 0, endDay: 5 },
  { index: 4, label: 'Q4 (6 Jan – 5 Apr)', startMonth: 0, startDay: 6, endMonth: 3, endDay: 5 }
]

// (month, day) as a single comparable integer, e.g. 6 Apr -> 0306.
const monthDayKey = (month: number, day: number) => month * 100 + day

// True if (month, day) falls within [start, end] inclusive, handling quarters (Q3, Q4)
// whose end month/day wraps around the calendar year (Dec -> Jan).
const isWithinQuarter = (month: number, day: number, quarter: QuarterDefinition): boolean => {
  const key = monthDayKey(month, day)
  const startKey = monthDayKey(quarter.startMonth, quarter.startDay)
  const endKey = monthDayKey(quarter.endMonth, quarter.endDay)

  if (startKey <= endKey) {
    return key >= startKey && key <= endKey
  }
  // Wraps the calendar year (Q3: Oct -> Jan, Q4: Jan -> Apr does not wrap, but kept
  // generic/symmetric for safety).
  return key >= startKey || key <= endKey
}

// Which of the four standard (fixed) HMRC MTD ITSA quarterly periods a date falls
// within, keyed on the UK tax-year Apr 6 boundary (not the calendar quarter).
export const getQuarterIndex = (value: Date | string): QuarterIndex => {
  const date = toUtcDate(value)
  const month = date.getUTCMonth()
  const day = date.getUTCDate()
  const match = QUARTERS.find((quarter) => isWithinQuarter(month, day, quarter))
  // Every day of the year is covered by exactly one of the four fixed quarters, so this
  // is unreachable, but keep a defensive fallback rather than a non-null assertion.
  return match ? match.index : 1
}

// Human-readable quarter label, e.g. 'Q1 (6 Apr – 5 Jul)'.
export const getQuarterLabel = (value: Date | string): string => {
  const index = getQuarterIndex(value)
  const quarter = QUARTERS.find((q) => q.index === index)
  return quarter ? quarter.label : QUARTERS[0].label
}
